import { lazy, Suspense, useEffect } from 'react';

import { observer } from 'mobx-react';
import { Outlet, useNavigate } from 'react-router-dom';

import ErrorBoundary from '../components/ErrorBoundary/ErrorBoundary';
import LiveBanner from '../components/LiveBanner';
import { Loader } from '../components/Loader/Loader';
import AppPortal from '../components/portals/AppPortal';
import { UserCoolDownGate } from '../features/auth/UserCooldownDialog/UserCoolDownGate';
import { useCrownRipsFeature } from '../hooks/useCrownRipsFeature';
import { useHideLoader } from '../hooks/useHideLoader';
import { LocaleProvider } from '../providers';
import { useShellDeepLinks, useShellUiReady } from '../services/nativeBridge/useShellRouting';
import { navigationService } from '../services/navigation';
import { StoreProvider } from '../stores/StoreProvider';

const DebugEnv =
  import.meta.env.MODE === 'production'
    ? null
    : lazy(() => import('../components/DebugEnv').then((m) => ({ default: m.DebugEnv })));

// Its own observer, so the auth and profile reads behind `ui.ready` re-render nothing but this.
const ShellUiReady = observer(() => {
  useShellUiReady();
  return null;
});

const LayoutContent = observer(() => {
  useHideLoader();
  useCrownRipsFeature();
  useShellDeepLinks();
  const navigate = useNavigate();

  useEffect(() => {
    navigationService.setNavigate(navigate);
  }, [navigate]);

  return (
    <LocaleProvider>
      <Suspense fallback={<Loader fullScreen />}>
        <ErrorBoundary>
          <LiveBanner />
          <Outlet />
        </ErrorBoundary>
      </Suspense>
      <AppPortal />
      <ShellUiReady />
      <UserCoolDownGate />
      {DebugEnv && (
        <Suspense fallback={null}>
          <DebugEnv />
        </Suspense>
      )}
    </LocaleProvider>
  );
});

export default function Layout() {
  return (
    <StoreProvider>
      <LayoutContent />
    </StoreProvider>
  );
}
